/** Tiny shared helpers + cross-module hooks for the auto-tracking modules. */
import { safe } from '../core/safe';

export const doc = document;

/** Wrapper (not a bound alias) so test fake timers still apply. */
export const now = (): number => Date.now();

/** Per-page-view reset hooks (scroll thresholds, error dedupe, search flag …). */
export const navH: (() => void)[] = [];
/**
 * Visibility hooks, run by pageviews.ts on every visibilitychange (with
 * `hidden`) and on pagehide (with `true, true` — the page is going away, not
 * just backgrounded) — BEFORE the transport flush listeners, which register
 * later in init.
 */
export const visH: ((hidden: boolean, unload?: boolean) => void)[] = [];
/** Event hooks: every queued event's name, after it is queued (forms: success events). */
export const evH: ((name: string) => void)[] = [];

/** addEventListener, always passive + safe(); capture optional. */
export function on<E extends Event>(
	t: EventTarget,
	type: string,
	fn: (ev: E) => void,
	capture?: boolean,
): void {
	t.addEventListener(type, safe(fn) as EventListener, { passive: true, capture });
}

/** Run when the main thread is idle (rIC with setTimeout fallback). */
export function idle(fn: () => void): void {
	if (typeof requestIdleCallback == 'function') requestIdleCallback(fn);
	else setTimeout(fn, 250);
}

/** Subtrees whose text no sighted visitor reads: svg tooltip/description
 *  metadata (an icon's <title>Fútbol</title> must not double the visible
 *  label into "FútbolFútbol") and non-rendered containers. */
const NON_VISIBLE = /^(title|desc|defs|symbol|style|script|template|noscript)$/i;

/**
 * The text a visitor actually SEES — the one definition every captured label
 * (click text, zone items, headings, field labels) reads through, so svg
 * <title> tooltips, [hidden] nodes and aria-hidden decoration (icon-font
 * ligatures like "shopping_cart") can never leak into analytics rows. A pure
 * node walk, not innerText: no forced reflow on the click path, deterministic
 * in test DOMs, and text joins exactly like textContent so well-behaved
 * historical zone item keys keep their identity. CSS-only hiding (class-based
 * display:none, sr-only clipping) would need computed style per node and
 * deliberately stays out of scope.
 */
export function visibleText(el: Element): string {
	let s = '';
	for (let n = el.firstChild; n; n = n.nextSibling) {
		if (n.nodeType == 3) s += n.nodeValue;
		else if (
			n.nodeType == 1 &&
			!NON_VISIBLE.test((n as Element).tagName) &&
			!(n as Element).hasAttribute('hidden') &&
			(n as Element).getAttribute('aria-hidden') != 'true'
		)
			s += visibleText(n as Element);
	}
	return s;
}
